<script setup lang="ts">
import type { SelectItemProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import { reactiveOmit } from '@vueuse/core'
import { SelectItem, SelectItemIndicator, useForwardProps } from 'reka-ui'
import { cn } from '#ui-kit/lib/utils'
import SelectItemText from './SelectItemText.vue'

const props = defineProps<
  SelectItemProps & {
    class?: HTMLAttributes['class']
    /**
     * Skip the default auto-wrap of slot content in `<SelectItemText>`. Use when
     * you need to compose richer item content (e.g. label + description, icon +
     * label + badge) and want to control which part appears in the trigger by
     * placing your own `<SelectItemText>` around just that part.
     */
    composed?: boolean
  }
>()

const delegatedProps = reactiveOmit(props, 'class', 'composed')

const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <SelectItem
    v-bind="forwardedProps"
    :class="
      cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-1 pr-9 text-sm text-gray-700 outline-hidden focus:bg-gray-100 focus:text-gray-900 data-highlighted:bg-gray-100 data-highlighted:text-gray-900 data-[state=checked]:bg-gray-50 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-5 [&_svg]:shrink-0',
        props.class,
      )
    "
  >
    <SelectItemText v-if="!composed">
      <slot />
    </SelectItemText>
    <slot v-else />
    <span class="absolute right-2 flex items-center justify-center">
      <SelectItemIndicator>
        <CheckCircleIcon class="text-green-500" />
      </SelectItemIndicator>
    </span>
  </SelectItem>
</template>
